import React, { Component } from 'react';
import { Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { toast } from 'react-toastify';
import XLSX from 'sheetjs-style';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Layout from './DashboardLayout';
import Loading from '../components/Loader';
import {
  MemberMultiSelectDropdown,
  MultiSelectItemDropdown,
} from '../components/common';
import {
  schedulesService,
  reportService,
  teamService,
  clientsService,
} from '../services';
import { PAGINATION_LIMIT, NO_DATA } from '../constants';
import Pagination from '../components/common/Pagination';

class OvertimeCommitment extends Component {
  state = {
    siteId: [],
    clientId: [],
    projectsList: [],
    membersList: [],
    overtimeSlotsList: [],
    projectId: [],
    teamMemberId: [],
    startDate: moment().startOf('day'),
    endDate: moment().endOf('day'),
    limit: PAGINATION_LIMIT,
    page: 1,
    numberOfDaysSelected: 1,
    daysLimit: 31,
    isLoading: false,
    showEdit: false,
    showEditIndex: null,
    clientsData: [],
    changeSource: null,
  };

  pageHeading = {
    preTitle: 'Reports',
    title: 'Overtime Commitments',
  };

  overtimeFormRef = React.createRef(null);

  memberSelectInputRef = React.createRef(null);

  projectSelectInputRef = React.createRef(null);

  clientSelectInputRef = React.createRef(null);

  siteSelectInputRef = React.createRef(null);

  componentDidMount() {
    this.getMembersAndProjects();
    this.getOvertimeSlots();
    this.getClientData();
  }

  componentDidUpdate(prevProps, prevState) {
    const { changeSource } = this.state;

    // Only act if changeSource has changed
    if (!changeSource || changeSource === prevState.changeSource) return;

    const formik = this.overtimeFormRef.current;
    const memberSelect = this.memberSelectInputRef?.current?.select;
    const clientSelect = this.clientSelectInputRef?.current?.select;
    const projectSelect = this.projectSelectInputRef?.current?.select;

    const clearSelect = (selectRef) => selectRef?.clearValue();
    const resetFields = (fields) =>
      fields.forEach((f) => formik.setFieldValue(f, []));

    switch (changeSource) {
      case 'site':
        resetFields(['clientId', 'projectId', 'teamMemberId']);
        [memberSelect, clientSelect, projectSelect].forEach(clearSelect);
        this.getClientData();
        setTimeout(() => {
          this.getMembersAndProjects();
        }, 200);
        break;

      case 'client':
        resetFields(['teamMemberId', 'projectId']);
        [memberSelect, projectSelect].forEach(clearSelect);
        this.getMembersAndProjects();

        break;

      case 'project':
        resetFields(['teamMemberId']);
        clearSelect(memberSelect);
        this.getMembersWithProject();

        break;

      default:
        break;
    }

    // Reset changeSource after handling to prevent re-trigger
    this.setState({ changeSource: null });
  }

  getClientData = () => {
    const { siteId } = this.state;
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      status: 'active',
      siteId,
    };
    if (
      (params.siteId && params.siteId.length === 0) ||
      (params.siteId && params.siteId.includes('all'))
    ) {
      delete params.siteId;
    }
    clientsService
      .get_client_list(params)
      .then((clientData) => {
        if (clientData.data) {
          this.setState((state) => ({
            ...state,
            isLoading: false,
            clientsData: [...clientData.data],
          }));
        }
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  getMembersAndProjects = () => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const { siteId, clientId } = this.state;

    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      siteId,
      clientId,
    };
    if (
      (params.siteId && params.siteId.length === 0) ||
      (params.siteId && params.siteId.includes('all'))
    ) {
      delete params.siteId;
    }
    if (
      (params.clientId && params.clientId.length === 0) ||
      (params.clientId && params.clientId.includes('all'))
    ) {
      delete params.clientId;
    }
    teamService
      .get_team_members_and_projects(params)
      .then((result) => {
        const { teamMembers, projects } = result.data;
        this.setState((state) => ({
          ...state,
          projectsList: [...projects],
          teamMembersList: [...teamMembers],
        }));
      })
      .finally(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  handleReFetchEvents = () => {
    this.getOvertimeSlots();
  };

  handleShowEdit = (status, id) => {
    this.setState((state) => ({
      ...state,
      showEdit: status,
      showEditIndex: status ? id : null,
    }));
  };

  handleUpdateSlot = (formData) => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      slotCount: formData.slotCount,
      otStart: formData.otStart,
      otEnd: formData.otEnd,
      slotId: formData.slotId,
    };

    schedulesService
      .update_overtime_slot(params)
      .then((result) => {
        if (result && result.status) {
          this.getOvertimeSlots();
          this.setState((state) => ({
            ...state,
            isLoading: false,
          }));
          this.handleShowEdit(false);
          // show success message
          toast.success('Overtime slot updated successfully');
        } else {
          toast.error(
            (result && result.data && result.data.message) ||
              'Something went wrong, please try again later.'
          );
        }
      })
      .catch((error) => {
        // show error message
        toast.error(
          (error && error.data) ||
            'Something went wrong, please try again later.'
        );
      })
      .finally(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  getOvertimeSlots = (param) => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const {
      startDate,
      endDate,
      page,
      limit,
      siteId,
      clientId,
      projectId,
      teamMemberId,
    } = this.state;
    const dataFromDate = moment(startDate).format('YYYY-MM-DD');
    const dataToDate = moment(endDate).format('YYYY-MM-DD');

    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      startDate: dataFromDate.concat(' 00:00:00'),
      endDate: dataToDate.concat(' 23:59:59'),
      page: (param && param.page) || page,
      teamMemberId: (param && param.teamMemberId) || teamMemberId,
      projectId: (param && param.projectId) || projectId,
      siteId: (param && param.siteId) || siteId,
      clientId: (param && param.clientId) || clientId,
      limit,
    };
    if (
      (params.siteId && params.siteId.length === 0) ||
      (params.siteId && params.siteId.includes('all'))
    ) {
      delete params.siteId;
    }
    if (
      (params.clientId && params.clientId.length === 0) ||
      (params.clientId && params.clientId.includes('all'))
    ) {
      delete params.clientId;
    }
    if (
      (params.projectId && params.projectId.length === 0) ||
      (params.projectId && params.projectId.includes('all'))
    ) {
      delete params.projectId;
    }
    if (
      (params.teamMemberId && params.teamMemberId.length === 0) ||
      (params.teamMemberId && params.teamMemberId.includes('all'))
    ) {
      delete params.teamMemberId;
    }
    reportService
      .get_slots_list(params)
      .then((result) => {
        const overtimeSlotsFinal =
          (result && result.data && result.data.data) || [];
        const metaData =
          (result && result.data && result.data.metadata[0]) || [];

        this.setState((state) => ({
          ...state,
          overtimeSlotsList: [...overtimeSlotsFinal],
          overtimeSlotMetaData: { ...metaData },
          isLoading: false,
        }));
      })
      .catch((error) => {
        // show error message
        toast.error(
          (error && error.data) ||
            'Something went wrong, please try again later.'
        );
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  getOvertimeSlotsExcel = () => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const {
      startDate,
      endDate,
      page,
      projectId,
      teamMemberId,
      clientId,
      siteId,
    } = this.state;
    const dataFromDate = moment(startDate).format('YYYY-MM-DD');
    const dataToDate = moment(endDate).format('YYYY-MM-DD');
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      startDate: dataFromDate.concat(' 00:00:00'),
      endDate: dataToDate.concat(' 23:59:59'),
      page,
      teamMemberId,
      projectId,
      siteId,
      clientId,
    };
    if (
      (params.siteId && params.siteId.length === 0) ||
      (params.siteId && params.siteId.includes('all'))
    ) {
      delete params.siteId;
    }
    if (
      (params.clientId && params.clientId.length === 0) ||
      (params.clientId && params.clientId.includes('all'))
    ) {
      delete params.clientId;
    }
    if (
      (params.projectId && params.projectId.length === 0) ||
      (params.projectId && params.projectId.includes('all'))
    ) {
      delete params.projectId;
    }
    if (
      (params.teamMemberId && params.teamMemberId.length === 0) ||
      (params.teamMemberId && params.teamMemberId.includes('all'))
    ) {
      delete params.teamMemberId;
    }
    reportService
      .get_slots_list(params)
      .then((result) => {
        const overtimeSlotsFinal = (result && result.data.data) || [];
        this.exportToExcel(overtimeSlotsFinal);
      })
      .catch((error) => {
        // show error message
        toast.error(
          (error && error.data) ||
            'Something went wrong, please try again later.'
        );
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  getMembersAndProjects = () => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const { siteId, clientId } = this.state;
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      siteId,
      clientId,
    };
    if (siteId.length === 0 || siteId.includes('all')) {
      delete params.siteId;
    }
    if (clientId.length === 0 || clientId.includes('all')) {
      delete params.clientId;
    }
    teamService
      .get_team_members_and_projects(params)
      .then((result) => {
        const { teamMembers, projects } = result.data;
        this.setState((state) => ({
          ...state,
          projectsList: [...projects],
          membersList: [...teamMembers],
        }));
      })
      .finally(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  handleDateSelect = (event, picker) => {
    picker.element.val(
      `${moment(picker.startDate).format('MMM D, YYYY')} - ${moment(
        picker.endDate
      ).format('MMM D, YYYY')}`
    );
    this.setState((state) => ({
      ...state,
      startDate: moment(picker.startDate),
      endDate: moment(picker.endDate),
    }));
  };

  exportToExcel = (param) => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    // Header row
    const header = [
      'Team Member',
      'Email',
      'Employee Id',
      'Project Name',
      'Date',
      'Time Slots',
      'Time Slots Count',
      'Time Slots Hours',
      'Type',
    ];

    const wsData = [
      header,
      ...param.map((row) => {
        const timeSlotString = row.timeSlots
          .filter((item) => item.otStartDateTime)
          .map(
            (slot) =>
              `${moment(slot.otStartDateTime).format('hh:mm A')} - ${moment(
                slot.otEndDateTime
              ).format('hh:mm A')}, `
          )
          .join('\n'); // line breaks in one cell
        return [
          row.name || '',
          row.email || '',
          row.employeeId || '',
          row.project || '',
          row.date ? moment(row.date).format('MMMM D, YYYY') : '',
          `${timeSlotString}`.replace(/,\s*$/, ''),
          row.timeSlotsCount || 0,
          row.timeSlotsHours || 0,
          row.scheduleType || '',
        ];
      }),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);

    // Style the first row (header)
    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '4F81BD' } }, // Blue background
      alignment: { horizontal: 'center', vertical: 'center' },
    };

    header.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex }); // r = 0 for first row
      if (!worksheet[cellRef]) return;
      worksheet[cellRef].s = headerStyle;
    });

    // Optional: set column width or wrap text for time slots
    worksheet['!cols'] = [
      { wch: 20 },
      { wch: 30 },
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 30 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
    ];

    // Enable wrap text for Time Slots column (index 4)
    // eslint-disable-next-line no-plusplus
    for (let r = 1; r < wsData.length; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: 4 }); // Column 4 = "Time Slots"
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          alignment: { wrapText: true },
        };
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'OT slots report');

    const currentDate = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Overtime_slots_report_${currentDate}.xlsx`);
    this.setState((state) => ({
      ...state,
      isLoading: false,
    }));
  };

  getMembersWithProject = () => {
    const { siteId, clientId, projectId } = this.state;
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      summaryList: true,
      siteId,
      clientId,
    };
    if (projectId.length > 0) {
      params.filter = {
        projectId,
      };
    }
    if (projectId.length === 0 || projectId.includes('all')) {
      delete params.filter;
    }
    if (siteId.length === 0 || siteId.includes('all')) {
      delete params.siteId;
    }
    if (clientId.length === 0 || clientId.includes('all')) {
      delete params.clientId;
    }

    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    teamService
      .get_team_members_with_project(params)
      .then((response) => {
        const { members } = response && response.data && response.data[0];
        this.setState((state) => ({
          ...state,
          membersList: [...members],
        }));
      })
      .finally(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  handleMemberChange = (selectedMembers) => {
    this.setState({ teamMemberId: selectedMembers });
  };

  handleSiteChange = (selectedSites) => {
    this.setState({
      siteId: selectedSites,
      changeSource: 'site',
    });
  };

  handleClientChange = (selelctedClient) => {
    this.setState({
      clientId: selelctedClient,
      changeSource: 'client',
    });
  };

  handleProjectChange = (selelctedProjectList) => {
    this.setState({
      changeSource: 'project',
      projectId: selelctedProjectList,
    });
  };

  render() {
    const {
      projectsList,
      startDate,
      endDate,
      isLoading,
      overtimeSlotsList,
      page,
      overtimeSlotMetaData,
      membersList,
      clientsData,
    } = this.state;
    const { hierarchy } = this.props;
    return (
      <Layout pageHeading={this.pageHeading}>
        <Formik
          initialValues={{
            siteId: [],
            clientId: [],
            projectId: [],
            teamMemberId: [],
            startDate,
            endDate,
            page: 1,
            limit: PAGINATION_LIMIT,
          }}
          onSubmit={(values) => {
            this.getOvertimeSlots(values);
          }}
          innerRef={this.overtimeFormRef}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit} className="mb-3">
              <Row>
                <Col>
                  <Row>
                    {hierarchy?.sites?.length > 0 && (
                      <Col md={6} lg={3}>
                        <MultiSelectItemDropdown
                          id="siteSelectInputRef"
                          name="siteId"
                          labelText="Sites"
                          fromArray={hierarchy.sites.map((site) => ({
                            value: site._id,
                            label: site.siteName,
                          }))}
                          formProps={formikprops}
                          isDisabled={hierarchy.sites.length < 1 && true}
                          innerRef={this.siteSelectInputRef}
                          isAllItemOption
                          isAllItemValuesWithNoArray
                          showErrorMessage
                          onChangeAction={this.handleSiteChange}
                        />
                      </Col>
                    )}
                    <Col md={6} lg={3}>
                      <MultiSelectItemDropdown
                        id="clientSelectInputRef"
                        name="clientId"
                        labelText="Clients"
                        fromArray={clientsData.map((client) => ({
                          value: client._id,
                          label: client.name,
                        }))}
                        formProps={formikprops}
                        isDisabled={clientsData.length < 1 && true}
                        innerRef={this.clientSelectInputRef}
                        isAllItemOption
                        isAllItemValuesWithNoArray
                        showErrorMessage
                        onChangeAction={this.handleClientChange}
                      />
                    </Col>
                    <Col md={6} lg={3}>
                      <MultiSelectItemDropdown
                        id="projectSelectInputRef"
                        name="projectId"
                        labelText="Projects"
                        fromArray={projectsList.map((project) => ({
                          value: project._id,
                          label: project.title,
                        }))}
                        formProps={formikprops}
                        isDisabled={projectsList.length < 1 && true}
                        innerRef={this.projectSelectInputRef}
                        isAllItemOption
                        isAllItemValuesWithNoArray
                        showErrorMessage
                        onChangeAction={this.handleProjectChange}
                      />
                    </Col>

                    <Col md={6} lg={3}>
                      <MemberMultiSelectDropdown
                        id="memberSelectInputRef"
                        name="teamMemberId"
                        labelText="Team Member(s)"
                        fromArray={membersList}
                        formProps={formikprops}
                        isDisabled={membersList.length < 1 && true}
                        innerRef={this.memberSelectInputRef}
                        isAllTeamMemberOption
                        isAllTeamMemberValuesWithNoArray
                        requiredValueAs="teamMemberId"
                        showErrorMessage
                        onChangeAction={this.handleMemberChange}
                      />
                    </Col>
                    <Col md={6} lg={3}>
                      <Form.Group>
                        <Form.Label>Date range</Form.Label>
                        <DateRangePicker
                          onEvent={this.handleDateSelect}
                          initialSettings={{
                            autoUpdateInput: false,
                            singleDatePicker: false,
                            startDate: moment().toDate(),
                            locale: {
                              format: 'MMM D, YYYY',
                            },
                            alwaysShowCalendars: true,
                            autoApply: true,
                            maxSpan: {
                              days: 30,
                            },
                          }}
                        >
                          <input
                            type="text"
                            className="form-control icon-calendar"
                            placeholder={`${moment().format(
                              'MMM D, YYYY'
                            )} - ${moment().format('MMM D, YYYY')}`}
                          />
                        </DateRangePicker>
                      </Form.Group>
                    </Col>
                    <Col md={6} lg={3}>
                      <Form.Group controlId="TeamMembers">
                        <Form.Label className="w-100">&nbsp;</Form.Label>
                        <Button variant="primary" type="Submit">
                          Generate
                        </Button>
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>

        <Row>
          <Col>
            <Card>
              <Card.Header>
                <Row className="align-items-center my-3">
                  <Col xs="auto">
                    <b>Overtime Commitments</b>
                  </Col>
                  {overtimeSlotsList && overtimeSlotsList.length > 0 && (
                    <Col xs="auto" className="ms-auto">
                      Download as{' '}
                      <Button
                        variant="white"
                        type="button"
                        size="sm"
                        className="mx-1"
                        onClick={() => this.getOvertimeSlotsExcel()}
                      >
                        Excel
                      </Button>
                    </Col>
                  )}
                </Row>
              </Card.Header>
              {overtimeSlotsList && overtimeSlotsList.length > 0 && (
                <div className="table-responsive">
                  <table
                    id="groupedDataByProject"
                    className="table table-sm table-hover card-table"
                  >
                    <thead className="thead-light">
                      <tr>
                        <th>Team Member</th>
                        <th>Email</th>
                        <th>Employee Id</th>
                        <th>Project Name</th>
                        <th>Date</th>
                        <th>Time Slots</th>
                        <th>Time Slots Count</th>
                        <th>Time Slots Hours</th>
                        <th>Type</th>
                        {/* <th>Action</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {overtimeSlotsList.map((item, i) => (
                        <tr key={i}>
                          <td>{item.name}</td>
                          <td>{item.email}</td>
                          <td>{item.employeeId}</td>
                          <td>{item.project}</td>
                          <td>{moment(item.date).format('MMMM D, YYYY')}</td>
                          <td>
                            <p className="mb-0">
                              {item &&
                                item.timeSlots &&
                                item.timeSlots
                                  .filter((it) => it.otStartDateTime)
                                  .map((ele, index, arr) => (
                                    <React.Fragment key={index}>
                                      {moment(ele.otStartDateTime).format(
                                        'hh:mm A'
                                      )}{' '}
                                      -{' '}
                                      {moment(ele.otEndDateTime).format(
                                        'hh:mm A'
                                      )}
                                      {index < arr.length - 1 && <br />}
                                    </React.Fragment>
                                  ))}
                            </p>
                          </td>
                          <td>{item.timeSlotsCount}</td>
                          <td>{item.timeSlotsHours}</td>
                          <td>{item.scheduleType}</td>
                        </tr>
                      ))}
                    </tbody>
                    {overtimeSlotMetaData &&
                      overtimeSlotMetaData.total > PAGINATION_LIMIT && (
                        <tfoot>
                          <tr>
                            <td colSpan="9">
                              <section className="d-flex justify-content-center py-3">
                                <Pagination
                                  className="pagination-bar"
                                  currentPage={page}
                                  totalCount={overtimeSlotMetaData.total}
                                  pageSize={PAGINATION_LIMIT}
                                  onPageChange={(pageN) => {
                                    this.setState({ page: pageN });
                                    this.getOvertimeSlots({
                                      page: pageN,
                                    });
                                  }}
                                />
                              </section>
                            </td>
                          </tr>
                        </tfoot>
                      )}
                  </table>
                </div>
              )}
            </Card>
          </Col>
        </Row>
        {!isLoading && overtimeSlotsList.length === 0 && (
          <Alert variant="info" className="text-center">
            {NO_DATA.OVERTIMESLOT}
          </Alert>
        )}
        {isLoading && <Loading contentAreaOnly />}
      </Layout>
    );
  }
}

const mapStateToProps = (state) => {
  const { hierarchy, permissionsReducer } = state;
  return {
    hierarchy,
    permissions: permissionsReducer,
  };
};

OvertimeCommitment.propTypes = {
  hierarchy: PropTypes.object,
};

export default connect(mapStateToProps)(OvertimeCommitment);
